import React from 'react'
import { graphql } from 'react-apollo'
import styled from 'styled-components'
import Cleave from 'cleave.js'
import PropTypes from 'prop-types'
import LoadingButton from '../common/LoadingButton'
import UpdateUseOfFundMutation from './queries/UpdateUseOfFundMutation'
import FadeInUpWrapper from '../common/FadeInUpWrapper'


const Container = styled(FadeInUpWrapper)`
  border: 1px solid #c1c1c1;
  border-radius: 5px;
  background: white;
  width: 500px;
`
const Header = styled.div`
  background: #f4f4f4;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border-bottom: 1px solid #cecece;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
`
const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin-bottom: 20px;
`
const BackContainer = styled.div`
  padding-left: 20px;
  align-self: center;
`
const Back = styled.a`
  cursor: pointer;
  text-decoration: underline;
`
const Close = styled.span`
  grid-column-start: 3;
  grid-column-end: 3;
  cursor: pointer;
  color: #4a4a4a;
  justify-self: end;
  align-self: center;
  padding-right: 20px;
  font-weight: bold;
  font-size: 1.2em;
`
const Row = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr .5fr;
  margin: 0px 40px 10px 40px;
`
const BorderRow = Row.extend`
  border-bottom: 1px solid #cecece;
  margin-top: 20px;
  padding-bottom: 5px;
`
const Error = styled.div`
  color: #c4535e;
  margin: 0px 40px 10px 40px;
  text-align: center;
  ${props => !props.visible ? 'visibility: hidden;' : ''}
`
const LeftCell = styled.div`
  text-align: right;
  padding-right: 20px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`
const RightCell = styled.div`
  padding-right: 10px;
  text-align: right;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
`
const LoanAmount = styled.span`
  margin-right: 8px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  text-align: right;
`
const Input = styled.input`
  align-self: end;
  width: 80%;
  padding-right: 8px;
  text-align: right;
  margin-bottom: 0px !important;
`
const TotalUse = Row.extend`
  background: #40bac9;
  color: white;
  margin: 0;
  padding: 10px 40px;
  font-weight: bolder;
`
const categories = {
  refinance: 'Refinance',
  realEstateMortgageRefinance: 'Real Estate Mortgage Refinance',
  realEstatePurchase: 'Real Estate Purchase',
  businessAcquisition: 'Business Acquisition',
  inventory: 'Supplies or Inventory',
  hiring: 'Hiring',
  marketing: 'Marketing',
  operationalCosts: 'Operational Cost',
  newEquipment: 'New Machinery or Equipment'
}

class UseOfProceeds extends React.Component {
  constructor(props) {
    super(props)
    const { useOfFund, cre } = props
    const useOfProceeds = {}
    for (const key in useOfFund) {
      if (key === 'realEstateMortgageRefinance' || key === 'realEstatePurchase') {
        if (cre) {
          useOfProceeds[key] = useOfFund[key]
        }
      }
      if (key !== 'total' && key !== 'remaining' && key !== '__typename') {
        useOfProceeds[key] = useOfFund[key]
      }
    }
    this.state = {
      total: useOfFund.total,
      remaining: useOfFund.remaining,
      loading: false,
      isButtonDisabled: useOfFund.remaining !== 0,
      useOfProceeds
    }
  }
  static propTypes = {
    cre: PropTypes.bool,
    loanAmount: PropTypes.number.isRequired,
    loanId: PropTypes.number.isRequired,
    onBackClick: PropTypes.func,
    onCancel: PropTypes.func,
    onClose: PropTypes.func,
    refetch: PropTypes.func.isRequired,
    reload: PropTypes.bool,
    toggleModal: PropTypes.func.isRequired,
    updateUseOfFund: PropTypes.func.isRequired,
    useOfFund: PropTypes.object.isRequired
  }
  componentDidMount() {
    this.setupCleaves()
  }
  componentWillUnmount() {
    this.destroyCleaves()
  }
  setupCleaves = () => {
    const { useOfProceeds } = this.state
    const cleaves = {}
    for (const category in useOfProceeds) {
      if (this[category]) {
        cleaves[category] = new Cleave(`#use_of_proceeds_${category}`, {
          numeral: true,
          numeralThousandsGroupStyle: 'thousand',
          numeralPositiveOnly: true,
          stripLeadingZeroes: true,
          numeralDecimalScale: 2,
          onValueChanged: e => this.onInputChange(e, category)
        })
      }
    }
    this.setState({ cleaves })
  }
  destroyCleaves = () => {
    const { cleaves } = this.state
    for (const category in cleaves) {
      cleaves[category].destroy()
    }
  }
  isTotalValid = () => {
    return this.props.loanAmount === this.state.total
  }
  onInputPaste = (e, category) => {
    const { loanAmount } = this.props
    const { useOfProceeds } = this.state
    e.preventDefault()
    const input = e.clipboardData.getData('text')
    const value = parseFloat(input.split(',').join(''), 10) // allow numbers with commas
    if (!Number.isNaN(value) && value >= 0) {
      const total = this.calculateTotal(category, value)
      const remaining = loanAmount - total
      this.setState({
        total,
        remaining,
        isButtonDisabled: remaining !== 0,
        useOfProceeds: {
          ...useOfProceeds,
          [category]: value
        }
      })
    }
  }
  calculateTotal = (category, newVal) => {
    const { useOfProceeds } = this.state
    const total = Object.keys(useOfProceeds).reduce((acc, cat) => {
      if (cat !== category) {
        acc += useOfProceeds[cat]
      }
      return acc
    }, 0)
    return total + newVal
  }
  onInputChange = (e, category) => {
    const { loanAmount } = this.props
    const { useOfProceeds } = this.state
    const input = e.target.value
    let value = parseFloat(input.split(',').join(''))
    if (Number.isNaN(value)) {
      value = 0
    }
    const total = this.calculateTotal(category, value)
    const remaining = loanAmount - total
    this.setState({
      total,
      remaining,
      isButtonDisabled: remaining !== 0,
      useOfProceeds: {
        ...useOfProceeds,
        [category]: value
      }
    })
  }
  renderError = () => {
    const { remaining } = this.state
    const floatRemaining = parseFloat(remaining.toFixed(2))
    const localeOptions = { minimumFractionDigits: 2, maximumFractionDigits: 2 }
    let message = `Please allocate the remaining $${floatRemaining.toLocaleString('en', localeOptions)}`
    if (remaining < 0) {
      message = `You have allocated $${(-floatRemaining).toLocaleString('en', localeOptions)} over the loan amount. Please reduce the allocation by $${(-floatRemaining).toLocaleString('en', localeOptions)}`
    }
    return (
      <Error visible={!this.isTotalValid()}>
        {message}
        {remaining > 0 && <br />}
      </Error>
    )
  }
  renderRows = () => {
    const { cre } = this.props
    const { useOfProceeds } = this.state
    const creCats = ['realEstateMortgageRefinance', 'realEstatePurchase']
    return Object.keys(useOfProceeds).map(category => {
      const disabled = category === 'refinance' || category === 'realEstateMortgageRefinance' || category === 'realEstatePurchase'
      if (!creCats.includes(category) && !cre || cre) {
        return (
          <Row key={category}>
            <LeftCell>
              {categories[category]}
            </LeftCell>
            <RightCell>
              <Input
                type="text"
                defaultValue={useOfProceeds[category] && useOfProceeds[category].toLocaleString({ minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                id={`use_of_proceeds_${category}`}
                innerRef={node => this[category] = node}
                disabled={disabled}
              />
            </RightCell>
          </Row>
        )
      }
    })
  }
  _onSave = async () => {
    const { updateUseOfFund, loanId, toggleModal, refetch, reload } = this.props
    const { businessAcquisition, inventory, hiring, marketing, operationalCosts, newEquipment } = this.state.useOfProceeds
    await this.setState({
      loading: true
    })
    try {
      await updateUseOfFund({
        variables: {
          input: {
            loanId,
            useOfFundFields: {
              businessAcquisition,
              inventory,
              hiring,
              marketing,
              operationalCosts,
              newEquipment
            }
          }
        }
      })
    } catch (err) {
      console.log(err)
    }
    if (reload) {
      return window.location.reload()
    }
    await refetch()
    toggleModal()
  }
  renderBackButton = () => {
    if (this.props.onBackClick) {
      return (
        <Back id="use_of_proceeds_back" onClick={this.props.onBackClick}>
          Back
        </Back>
      )
    }
  }
  renderCloseButton = () => {
    if (this.props.onClose) {
      return (
        <Close id="use_of_proceeds_close" onClick={this.props.onClose}>
          X
        </Close>
      )
    }
  }
  render() {
    const { loanAmount } = this.props
    const { total, loading, isButtonDisabled } = this.state
    return (
      <Container>
        <Header>
          <BackContainer>
            {this.renderBackButton()}
            {this.renderCloseButton()}
          </BackContainer>
          <h2>
            Use of Proceeds
          </h2>
        </Header>
        <BorderRow>
          <LeftCell>
            Loan Amount
          </LeftCell>
          <RightCell>
            <LoanAmount>
              {parseFloat(loanAmount.toFixed(2)).toLocaleString('en')}
            </LoanAmount>
          </RightCell>
        </BorderRow>
        {this.renderRows()}
        <TotalUse>
          <LeftCell>
            Total Use of Proceeds
          </LeftCell>
          <RightCell>
            <LoanAmount>
              {parseFloat(total.toFixed(2)).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </LoanAmount>
          </RightCell>
        </TotalUse>
        {this.renderError()}
        <ButtonContainer>
          <LoadingButton
            id="use_of_proceeds_save"
            onClick={this._onSave}
            loading={loading}
            disabled={isButtonDisabled}
          >
            Save use of proceeds
          </LoadingButton>
        </ButtonContainer>
      </Container>
    )
  }
}

export default graphql(UpdateUseOfFundMutation, {
  name: 'updateUseOfFund'
})(UseOfProceeds)
